import { useEffect, useRef, useState } from 'react';
import { vexStockImages } from '~/constants/environment';
import optimizeImage from '~/utilities/optimizeImage';
import {
  BASE_HERO_HEIGHT,
  computeDesktopEllipseTop,
  computeEllipseLeft,
  DESKTOP_ELLIPSE_MASK_MD,
  DESKTOP_ELLIPSE_MASK_XL,
  MOBILE_MASK,
  TABLET_MASK,
  TABLET_MASK_CANVAS_HEIGHT
} from './OrderStatusModule.masks';
import {
  DesktopEllipse,
  DesktopEllipseImg,
  TabletImg
} from './OrderStatusModule.styles';
import { CtaAnchorProps } from './utils';
import styles from './VehicleHeroImage.module.css';

type OwnProps = {
  children: React.ReactNode;
  ctaProps: CtaAnchorProps;
  imageUrl: string;
};

// Matches --spacing-4xl (2.5rem/40px) — minimum breathing room above and below the card content
// when it grows taller than the baseline hero.
const DESKTOP_VERTICAL_GUTTER = 40;

export default function VehicleHeroImage({
  children,
  ctaProps,
  imageUrl
}: OwnProps) {
  const desktopContentRef = useRef<HTMLDivElement>(null);
  const desktopHeroRef = useRef<HTMLDivElement>(null);
  const [desktopContentHeight, setDesktopContentHeight] = useState(0);
  const [desktopHeroWidth, setDesktopHeroWidth] = useState(1024);
  const [isGenericImage, setIsGenericImage] = useState(false);

  const desktopHeroHeight = Math.max(
    BASE_HERO_HEIGHT,
    desktopContentHeight + DESKTOP_VERTICAL_GUTTER * 2
  );
  const desktopEllipseLeft = computeEllipseLeft(desktopHeroWidth);

  const genericImageUrl = optimizeImage(
    `${vexStockImages}/generic_vehicle_desktop_1920x1080.png`,
    {
      crop: '20:9',
      width: 960,
      trim: '70,0,0'
    }
  );

  const desktopImageSrc = optimizeImage(imageUrl, {
    format: 'auto',
    trim: '0,0,80',
    width: 960
  });
  const imageSrc = optimizeImage(imageUrl, { format: 'auto' });

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src !== genericImageUrl) {
      e.currentTarget.src = genericImageUrl;
      setIsGenericImage(true);
    }
  };

  useEffect(() => {
    const el = desktopHeroRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDesktopHeroWidth(el.clientWidth));
    ro.observe(el);
    setDesktopHeroWidth(el.clientWidth);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = desktopContentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setDesktopContentHeight(el.clientHeight)
    );
    ro.observe(el);
    setDesktopContentHeight(el.clientHeight);

    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* ── Mobile (<768): full-bleed image with curved arc, card below ── */}
      <div className="bg-background-strong sm:hidden">
        <a
          aria-hidden="true"
          data-testid="vehicle-hero-image-cta"
          tabIndex={-1}
          {...ctaProps}
        >
          <img
            alt=""
            aria-hidden="true"
            className="block h-[210px] w-full select-none object-cover"
            onError={handleImageError}
            src={imageSrc}
            style={MOBILE_MASK}
          />
        </a>
        <div className="mx-auto w-full max-w-[1352px] p-2xl">{children}</div>
      </div>

      {/* ── Tablet (768–1023): ellipse reveal, card overlays left zone ── */}
      <div
        className="relative hidden w-full overflow-hidden bg-background-strong sm:block md:hidden"
        style={{ minHeight: BASE_HERO_HEIGHT }}
      >
        <a
          aria-hidden="true"
          data-testid="vehicle-hero-image-cta"
          tabIndex={-1}
          {...ctaProps}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 select-none"
          style={{ ...TABLET_MASK, height: TABLET_MASK_CANVAS_HEIGHT }}
        >
          <TabletImg
            alt=""
            aria-hidden="true"
            onError={handleImageError}
            src={imageSrc}
          />
        </a>
        <div
          className="relative flex w-full flex-col justify-center py-4xl"
          style={{ minHeight: BASE_HERO_HEIGHT }}
        >
          <div className="mx-auto w-full max-w-[1352px] px-3xl">
            <div className="max-w-[420px]">{children}</div>
          </div>
        </div>
      </div>

      {/* ── Desktop (≥1024): card on left, ellipse image on right ── */}
      <div
        ref={desktopHeroRef}
        className="relative hidden w-full overflow-hidden bg-background-strong md:block"
        style={{ height: desktopHeroHeight }}
      >
        <div className="relative mx-auto h-full w-full max-w-[1352px]">
          <div className="relative z-[2] flex h-full max-w-[735px] flex-col justify-center pl-4xl py-4xl">
            <div ref={desktopContentRef}>{children}</div>
          </div>
          <div
            aria-hidden="true"
            className="absolute inset-y-0 left-full z-[1] w-0"
          >
            <DesktopEllipse
              aria-hidden="true"
              data-testid="vehicle-hero-image-cta"
              tabIndex={-1}
              {...ctaProps}
              className={styles.ellipse}
              style={
                {
                  '--ellipse-mask-md': DESKTOP_ELLIPSE_MASK_MD,
                  '--ellipse-mask-xl': DESKTOP_ELLIPSE_MASK_XL,
                  left: desktopEllipseLeft,
                  top: computeDesktopEllipseTop(
                    desktopHeroHeight,
                    desktopHeroWidth
                  )
                } as React.CSSProperties
              }
            >
              <DesktopEllipseImg
                isGeneric={isGenericImage}
                onError={handleImageError}
                src={desktopImageSrc}
              />
            </DesktopEllipse>
          </div>
        </div>
      </div>
    </>
  );
}
